import http from '@utils/http';
import { ENV } from '@configs/env';

/**
 * Normalize notification item: API returns readAt (ISO string | null); we add read boolean.
 */
const normalizeItem = (item) => ({
    ...item,
    read: !!(item.readAt ?? item.read_at),
});

/**
 * GET /api/v1/notifications
 * Uses shared http (request interceptor adds Bearer token). On 401, http interceptor refreshes token and retries.
 * @param {{ page?: number, limit?: number }} params
 */
export const getNotifications = (params = {}) => {
    return http.get('/notifications', { params }).then((res) => {
        const raw = res.data?.data ?? res.data;
        const data = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
        const items = data.map(normalizeItem);
        const total = res.data?.total ?? raw?.total ?? items.length;
        return { items, total };
    });
};

/**
 * GET /api/v1/notifications/count — total unread for the current user (not limited by list page size).
 * Response shapes vary; normalize to a non-negative integer.
 */
export const getNotificationsUnreadCount = () =>
    http.get('/notifications/count').then((res) => {
        const raw = res.data?.data ?? res.data;
        if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, Math.floor(raw));
        if (raw && typeof raw === 'object') {
            const n =
                raw.unreadCount ??
                raw.unread_count ??
                raw.unread ??
                raw.count ??
                raw.totalUnread ??
                raw.total_unread;
            if (typeof n === 'number' && Number.isFinite(n)) return Math.max(0, Math.floor(n));
        }
        return -1;
    });

/**
 * PATCH /api/v1/notifications/:id/read
 */
export const markNotificationRead = (id) =>
    http.patch(`/notifications/${id}/read`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/notifications/read-all
 */
export const markAllNotificationsRead = () =>
    http.patch('/notifications/read-all').then((res) => res.data?.data ?? res.data);

/**
 * Full URL for SSE stream. Use with fetch() and Authorization header (EventSource cannot send headers).
 */
export const getNotificationsStreamUrl = () => {
    // Same base as axios unless VITE_API_URL_STREAM is set (e.g. different host for SSE).
    const raw = (ENV.API_URL_STREAM && String(ENV.API_URL_STREAM).trim()) || ENV.API_URL || '';
    const base = raw.replace(/\/$/, '');
    return `${base}/notifications/stream`;
};
